// this needs to be the first import since it determines the cdn location of resources
import "./publicPath.ts";

import * as Sentry from "@sentry/browser";
import "core-js/web";
import "whatwg-fetch";
import "./helpers/nodelist-foreach-polyfill";
import "./i18n/i18n";
import {RootBannerSection} from "./components/swipers/rootBannerSection";
import {loadFlashsales} from "./helpers/loadFlashsales";
import {RootFlashsaleSection} from "./components/swipers/rootFlashsaleSection";
import {RedesignFlashSaleSection} from "./components/organisms/flashsale/redesignFlashsaleSection";
import {TeaserSwiper, TeaserSwiperRedesign} from "./components/swipers/teaserSlider";
import {BlogImageSwiper} from "./components/swipers/blogImageSwiper";
import {ProductSwiper} from "./components/swipers/productSwiper";
import {BlogGallerySwiper} from "./components/swipers/blogGallerySwiper";
import {BwareSwiper} from "./components/swipers/bwareSwiper";
import {ShopRatingHandler} from "./components/ShopRating/shopRatingHandler";
import PinitComponent from "./components/pinitComponent";
import {init as initCart} from "./index-cart";
import EnergyLabelPortal from "./components/energyLabel/EnergyLabelPortal";
import NewsletterRegistrationPagePortal from "./components/newsletter/NewsletterRegistrationPagePortal";
import {ProductBundleHandler} from "./components/ProductView/PdpBundle";
import {beforeBreadcrumb} from "./helpers/sentryHelper";
import PublitasPortal from "./components/catalogues/PublitasPortal";
import TopCategories from "./components/categories/TopCategories";
import BlogOverviewSwiper from "./components/organisms/blog/BlogOverviewSwiper";
import HomepageBannerSwiper from "./components/organisms/homepageBannerSlider/HomepageBannerSwiper";
import {flashSaleTimers} from "./components/atoms/FlashSaleTimer";
import {AdvantageBannerSwiper, AdvantageBannerSwiperRedesign} from "./components/swipers/advantageBannerSwiper";
import PayPalPayLaterMessagePortal from "./components/lib/Banner/PayPalPayLaterMessage";

Sentry.init({
  dsn: "https://17a766be243d431cb89d9487d11259fe@sentry.io/1299670",
  enabled: __PRODUCTION__ || !!new URLSearchParams(window.location.search).get("sentry"),
  normalizeDepth: 5,
  beforeBreadcrumb,
  beforeSend: (event) => {
    if (event.exception?.values && event.exception.values[0] && event.exception.values[0].value) {
      /* Check for an error caused by `t.js` (Particular Audience, injected by GTM) */
      if (
        [
          "Argument 1 ('target') to MutationObserver.observe must be an instance of Node",
          "MutationObserver.observe: Argument 1 is not an object" /* Firefox */,
        ].includes(event.exception.values[0].value)
      ) {
        // Just ignore that error
        return null;
      }

      // Most likely a page leave on a non-returned fetch
      if (
        ["Load failed", "Failed to fetch"].includes(event.exception.values[0].value) &&
        event.exception.values[0].type === "TypeError"
      ) {
        return null;
      }
    }

    return event;
  },
});

if (__DEVELOPMENT__) {
  // eslint-disable-next-line no-console
  console.log("%cDevelopment", "color: red; font-size: x-large;");
}

initCart();

// Initialisierung Swiper Homepage
const bannerSection: HTMLElement | null = document.getElementById("BannerSection");
if (bannerSection !== null) {
  new RootBannerSection(bannerSection);
}

const flashsaleSection: HTMLElement | null = document.getElementById("FlashsaleSection");
if (flashsaleSection !== null) {
  loadFlashsales().then((flashSaleData) => {
    new RootFlashsaleSection(flashsaleSection, flashSaleData);
  }, Sentry.captureException);
}

// Connox Deals Swiper (Redesign)
const flashsaleSectionRedesign: HTMLElement | null = document.getElementById("FlashsaleSectionRedesign");
if (flashsaleSectionRedesign !== null) {
  loadFlashsales().then((flashSaleData) => {
    new RedesignFlashSaleSection(flashsaleSectionRedesign, flashSaleData);
  }, Sentry.captureException);
}

// Product slider in blog entries and in longtext (TextPlaceholder)
const swiperProducts: NodeListOf<HTMLDivElement> | null = document.querySelectorAll(".swiper-products");
const swiperProductsRedesign: NodeListOf<HTMLDivElement> | null =
  document.querySelectorAll(".swiper-products-redesign");

if (swiperProducts.length > 0) {
  swiperProducts.forEach((swiper) => {
    new TeaserSwiper(swiper);
  });
}
if (swiperProductsRedesign.length > 0) {
  swiperProductsRedesign.forEach((swiper) => {
    TeaserSwiperRedesign.run(swiper);
  });
}

// Mobiler Vorteile Banner Slider
const swiperAdvantageBanner: HTMLElement | null = document.querySelector("#AdvantageBannerSwiper");
const swiperAdvantageBannerRedesign: HTMLElement | null = document.querySelector("#AdvantageBannerSwiperRedesign");
if (swiperAdvantageBanner) {
  new AdvantageBannerSwiper(swiperAdvantageBanner);
}
if (swiperAdvantageBannerRedesign) {
  AdvantageBannerSwiperRedesign.run(swiperAdvantageBannerRedesign);
}

// Blog images, Coverflow style
const swiperBlogImages: NodeListOf<HTMLDivElement> = document.querySelectorAll(".swiper-blog-images");
if (swiperBlogImages.length > 0) {
  swiperBlogImages.forEach((swiper) => {
    new BlogImageSwiper(swiper);
  });
}

// Initialisierung Swiper Produktseiten
const swiperContainerProduct: Element = document.getElementsByClassName("swiper-container-product")[0];
const zoomAnchorList: HTMLCollectionOf<Element> = document.getElementsByClassName("gallery-zoom");
if (swiperContainerProduct !== null) {
  new ProductSwiper(swiperContainerProduct, zoomAnchorList);
}

// Initialisierung Swiper Blog-Einträge - Galerie
const gallerySwiper: HTMLDivElement | null = document.querySelector(".swiper-container-gallery");
if (gallerySwiper !== null) {
  new BlogGallerySwiper(gallerySwiper);
}

// Alle Swiper B-Waren-Seite
const galleryLinkCollection = document.querySelectorAll<HTMLAnchorElement>(".gallery-link");
if (galleryLinkCollection.length > 0) {
  galleryLinkCollection.forEach((anchor) => {
    new BwareSwiper(anchor);
  });
}

// Asynchronous shoprating loading
const shopRatingElement: HTMLElement | null = document.querySelector("[data-react-rating]");
const headlineRating: HTMLElement | null = document.querySelector(".product-rating");
if (shopRatingElement) {
  ShopRatingHandler.run(shopRatingElement, headlineRating);
}

const blogEntryContentElement = document.querySelector<HTMLElement>(".blog-entry__content");
if (blogEntryContentElement) {
  PinitComponent.pinit(blogEntryContentElement);
}

const newsletterRegistrationPage = document.getElementById("newsletter-landing-page");
if (newsletterRegistrationPage) {
  NewsletterRegistrationPagePortal.run(newsletterRegistrationPage);
}

const energyLabelElements = document.querySelectorAll<HTMLElement>("[data-energy-class]");
if (energyLabelElements.length) {
  EnergyLabelPortal.run(energyLabelElements);
}

const productBundleContainer = document.getElementById("product-bundle-container");
if (productBundleContainer) {
  ProductBundleHandler.run(productBundleContainer as HTMLDivElement);
}

const publitasContainer = document.getElementById("publitas-embed-generic-container");
if (publitasContainer) {
  PublitasPortal.run(publitasContainer as HTMLDivElement);
}

const topCategory: HTMLElement | null = document.querySelector("#topCategoriesPlaceholder");
if (topCategory) {
  TopCategories.run(topCategory);
}

const rootBlogOverviewSection: HTMLElement | null = document.querySelector("#blogOverviewPlaceholder");
if (rootBlogOverviewSection) {
  BlogOverviewSwiper.run(rootBlogOverviewSection);
}

// For the Connox Deals overview page, as well as for the single Connox Deals pages
const flashSaleTimerAnchors: NodeListOf<HTMLAnchorElement> = document.body.querySelectorAll(".fill.flash-sale");
if (flashSaleTimerAnchors.length) {
  flashSaleTimers(flashSaleTimerAnchors);
}

const redesignHomepageBannerSection: HTMLElement | null = document.querySelector("#BannerSectionRedesign");
if (redesignHomepageBannerSection) {
  HomepageBannerSwiper.run(redesignHomepageBannerSection);
}

PayPalPayLaterMessagePortal.run();
